/**
 * abilities.ts — player-triggered timed boosts on cooldowns. They reuse the
 * existing buff stack (addModifier), so their effect aggregates automatically in
 * computeMultipliers and expires via pruneModifiers. Because abilities are only
 * invoked from the UI (like clickMine), they never run in the tick loop and pose
 * no determinism concern.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { ABILITY_BY_ID, BALANCE } from '../data';
import { meetsRequirement } from './unlock';
import { addModifier } from './modifiers';

/** Abilities unlock once the player has some lifetime earnings. */
export function abilitiesUnlocked(state: GameState): boolean {
  return state.stats.lifetimeEarnings >= BALANCE.abilities.unlockLifetime;
}

export function abilityUnlocked(state: GameState, id: string): boolean {
  const a = ABILITY_BY_ID[id];
  return !!a && abilitiesUnlocked(state) && meetsRequirement(state, a.requires);
}

/** Cooldown remaining in ms (0 = ready). */
export function abilityCooldownRemaining(state: GameState, id: string): number {
  const readyAt = state.abilities[id]?.readyAt ?? 0;
  return Math.max(0, readyAt - state.meta.gameTimeMs);
}

/** Whether the ability's timed effect is currently active. */
export function abilityActive(state: GameState, id: string): boolean {
  return state.modifiers.some((m) => m.id === `ability:${id}`);
}

/** Active effect remaining in ms (0 = inactive). */
export function abilityActiveRemaining(state: GameState, id: string): number {
  const mod = state.modifiers.find((m) => m.id === `ability:${id}`);
  return mod ? Math.max(0, mod.endsAt - state.meta.gameTimeMs) : 0;
}

export function abilityReady(state: GameState, id: string): boolean {
  return abilityUnlocked(state, id) && abilityCooldownRemaining(state, id) <= 0;
}

/** Activate an ability: push its buff and start its cooldown. */
export function activateAbility(state: GameState, id: string, bus?: EventBus): boolean {
  const a = ABILITY_BY_ID[id];
  if (!a || !abilityReady(state, id)) return false;
  const now = state.meta.gameTimeMs;
  addModifier(state, {
    id: `ability:${id}`,
    label: a.name,
    effect: a.effect,
    endsAt: now + a.duration * 1000,
  });
  state.abilities[id] = { readyAt: now + a.cooldown * 1000 };
  state.stats.abilitiesUsed += 1;
  bus?.emit('ability:activated', { abilityId: id, name: a.name });
  bus?.emit('notify', { kind: 'reward', message: `${a.icon} ${a.name} aktiviert!` });
  return true;
}
